import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../AuthProvider";
import http from "../../api/apiClient";
import { useNavigate } from "react-router-dom";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CopyButton from "../../components/CopyButton";
import { FaPlus, FaTimes, FaUsersCog } from "react-icons/fa";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { UserHero, UserPageShell } from "./components";

type UserRecord = Record<string, any> & {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  realmRoles?: string[];
};

type DeleteModalState = {
  show: boolean;
  userId: string | null;
};

const normalizeTimestamp = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && numeric > 0) {
    const millis = numeric < 1e12 ? numeric * 1000 : numeric;
    return millis;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const Users = () => {
  const { keycloak } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({ show: false, userId: null });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const hasFetchedRef = useRef(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/users");
      const baseUsers = Array.isArray(data) ? data : [];
      const enrichedUsers = await Promise.all(
        baseUsers.map(async (user) => {
          if (!user?.id) return user;
          try {
            const { data: sessions } = await http.get(`/admin/users/${user.id}/sessions`);
            if (!Array.isArray(sessions) || !sessions.length) return user;
            let latestTs = null;
            sessions.forEach((session) => {
              const ts = normalizeTimestamp(
                session?.lastAccess ??
                  session?.last_access ??
                  session?.lastAccessTime ??
                  session?.last_login ??
                  session?.lastLogin ??
                  session?.lastLoginTime ??
                  session?.started ??
                  session?.start ??
                  session?.startTime
              );
              if (ts && (!latestTs || ts > latestTs)) {
                latestTs = ts;
              }
            });
            if (!latestTs) return user;
            return { ...user, lastAccess: latestTs };
          } catch {
            return user;
          }
        })
      );
      setUsers(enrichedUsers);
      setError(null);
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!keycloak?.token || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchUsers();
  }, [keycloak?.token, fetchUsers]);

  const handleRetry = () => {
    setError(null);
    fetchUsers();
  };

  const handleAddUser = () => {
    navigate("new");
  };

  const handleDeleteUser = async (userId) => {
    try {
      await http.delete(`/admin/users/${userId}`);
      fetchUsers();
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setDeleteModal({ show: false, userId: null });
    }
  };

  const formatDateTime = useCallback(
    (raw) => {
      if (raw === undefined || raw === null || raw === "") return "—";
      const toDate = (value) => {
        if (value === undefined || value === null || value === "") return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
        const num = Number(value);
        if (!Number.isNaN(num) && num > 0) {
          const millis = num < 1e12 ? num * 1000 : num;
          const asDate = new Date(millis);
          return Number.isNaN(asDate.getTime()) ? null : asDate;
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };
      const date = toDate(raw);
      if (!date) return "—";
      try {
        return date.toLocaleString(i18n.language || undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
      } catch {
        return date.toLocaleString();
      }
    },
    [i18n.language]
  );

  const usersWithRoles = useMemo(
    () =>
      users.map((user) => ({
        ...user,
        realmRoles: Array.isArray(user.realmRoles) ? user.realmRoles : [],
      })),
    [users]
  );

  const availableRoles = useMemo(() => {
    const roles = new Set<string>();
    usersWithRoles.forEach((user) => {
      user.realmRoles.forEach((role) => roles.add(role));
    });
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [usersWithRoles]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return usersWithRoles.filter((user) => {
      const fields = [
        user.username,
        user.email,
        user.firstName,
        user.lastName,
        ...(Array.isArray(user.realmRoles) ? user.realmRoles : []),
      ]
        .filter(Boolean)
        .map((value) => value.toString().toLowerCase());
      const matchesSearch = term ? fields.some((field) => field.includes(term)) : true;
      const matchesRole = roleFilter ? user.realmRoles.includes(roleFilter) : true;
      return matchesSearch && matchesRole;
    });
  }, [search, usersWithRoles, roleFilter]);

  if (loading) return <Spinner />;
  if (error) {
    return <ErrorPage message={error} status={error?.status} onRetry={handleRetry} />;
  }

  const pageTitle = t("admin.users.title", { defaultValue: "Users" });
  const addUserLabel = t("admin.users.add_button", { defaultValue: "Add User" });
  const activeBadge = t("common.badges.active", { defaultValue: "Active" });
  const inactiveBadge = t("common.badges.inactive", { defaultValue: "Inactive" });
  const lastAccessHeader = t("admin.users.last_access", { defaultValue: "Last access" });
  const nameHeader = t("common.name", { defaultValue: "Name" });
  const totalUsersLabel = t("admin.users.total_count", { defaultValue: "Total users" });
  const rolesHeader = t("admin.users.roles", { defaultValue: "Roles" });
  const noRolesLabel = t("admin.users.no_roles", { defaultValue: "No roles" });
  const viewLabel = t("common.actions.view", { defaultValue: "View" });
  const deleteLabel = t("common.actions.delete", { defaultValue: "Delete" });
  const searchPlaceholder = t("admin.users.search_placeholder", { defaultValue: "Search users" });
  const clearSearchLabel = t("common.actions.clear_search", { defaultValue: "Clear search" });
  const noUsersLabel = t("admin.users.none_available", { defaultValue: "No users found" });
  const roleFilterLabel = t("admin.users.role_filter.label", { defaultValue: "Filter by role" });
  const roleFilterAll = t("admin.users.role_filter.all", { defaultValue: "All roles" });
  const clearRoleFilterLabel = t("admin.users.role_filter.clear", { defaultValue: "Clear role filter" });

  const getDisplayName = (user) => {
    const combined = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    return combined || user.username || user.email || "—";
  };

  return (
    <UserPageShell>
      <UserHero
        icon={FaUsersCog}
        eyebrow={t("navigation.management", { defaultValue: "Beheer" })}
        title={pageTitle}
        description={t("admin.users.description", {
          defaultValue: "Manage staff accounts, access and roles.",
        })}
        actions={
          <button
            type="button"
            onClick={handleAddUser}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
          >
            <FaPlus aria-hidden="true" />
            {addUserLabel}
          </button>
        }
      />

      <div className="lds-section-enter flex flex-col gap-4 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:w-2/5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500 dark:hover:border-slate-600"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute inset-y-0 right-0 flex items-center justify-center pr-3 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              aria-label={clearSearchLabel}
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1.5 font-semibold text-indigo-700 shadow-sm dark:border-indigo-500/30 dark:bg-indigo-900/40 dark:text-indigo-200">
            {totalUsersLabel}: <span className="font-bold">{filteredUsers.length}</span>
          </div>
          <label className="flex items-center gap-2">
            <span className="whitespace-nowrap">{roleFilterLabel}</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-950 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="">{roleFilterAll}</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
          {roleFilter && (
            <button
              type="button"
              onClick={() => setRoleFilter("")}
              className="p-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              aria-label={clearRoleFilterLabel}
              title={clearRoleFilterLabel}
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      <LdsTableCard className="lds-section-enter overflow-hidden">
        <LdsTableScroll style={{ overflowY: "visible" }}>
        <LdsTable>
          <LdsTableHead>
            <LdsTableRow>
              <LdsTableHeadCell>{nameHeader}</LdsTableHeadCell>
              <LdsTableHeadCell>Email</LdsTableHeadCell>
              <LdsTableHeadCell>Status</LdsTableHeadCell>
              <LdsTableHeadCell>{lastAccessHeader}</LdsTableHeadCell>
              <LdsTableHeadCell>{rolesHeader}</LdsTableHeadCell>
              <LdsTableHeadCell>{t("common.actions.name", { defaultValue: "Actions" })}</LdsTableHeadCell>
            </LdsTableRow>
          </LdsTableHead>
          <LdsTableBody className="[&>tr]:animate-[lds-section-enter_360ms_cubic-bezier(0.22,1,0.36,1)_both]">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <LdsTableRow
                  key={user.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                >
                  <LdsTableCell className="font-semibold text-slate-950 dark:text-white">{getDisplayName(user)}</LdsTableCell>
                  <LdsTableCell>
                    <span className="inline-flex max-w-full items-center gap-1">
                      <span className="min-w-0 break-all">{user.email || "—"}</span>
                      <CopyButton
                        value={user.email}
                        label={t("common.copy_email", { defaultValue: "Copy email address" })}
                      />
                    </span>
                  </LdsTableCell>
                  <LdsTableCell>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                        user.enabled
                          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200"
                          : "bg-red-50 text-red-700 dark:bg-red-400/10 dark:text-red-200"
                      }`}
                    >
                      {user.enabled ? activeBadge : inactiveBadge}
                    </span>
                  </LdsTableCell>
                  <LdsTableCell>
                    {formatDateTime(
                      user.lastAccess ??
                        user.lastAccessAt ??
                        user.lastAccessed ??
                        user.lastLogin ??
                        user.lastLoginTimestamp ??
                        user.attributes?.lastAccess?.[0] ??
                        user.attributes?.last_access?.[0]
                    )}
                  </LdsTableCell>
                  <LdsTableCell>
                    {user.realmRoles.length === 0 ? (
                      <span className="text-gray-400 dark:text-gray-500">{noRolesLabel}</span>
                    ) : (
                      <ul className="list-disc list-inside text-left text-gray-700 dark:text-gray-200">
                        {user.realmRoles.map((role) => (
                          <li key={role}>{role}</li>
                        ))}
                      </ul>
                    )}
                  </LdsTableCell>
                  <LdsTableCell>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/users/${user.id}`);
                        }}
                        className="lumen-glow-sheen inline-flex items-center rounded-full border border-sky-200/60 bg-sky-50/70 px-3 py-1.5 text-sm font-semibold text-sky-700 transition hover:text-sky-900 dark:border-sky-500/40 dark:bg-sky-900/40 dark:text-sky-200"
                      >
                        {viewLabel}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({ show: true, userId: user.id });
                        }}
                        className="lumen-glow-sheen inline-flex items-center rounded-full border border-red-100/60 bg-red-50/80 px-3 py-1.5 text-sm font-semibold text-red-600 transition hover:text-red-700 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200"
                      >
                        {deleteLabel}
                      </button>
                    </div>
                  </LdsTableCell>
                </LdsTableRow>
              ))
            ) : (
              <LdsTableRow>
                <LdsTableCell colSpan={6} className="text-center text-slate-500 dark:text-slate-400">
                  {noUsersLabel}
                </LdsTableCell>
              </LdsTableRow>
            )}
          </LdsTableBody>
        </LdsTable>
        </LdsTableScroll>
      </LdsTableCard>

      <Modal
        show={deleteModal.show}
        type="confirm"
        title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
        message={t("admin.users.confirm_delete", {
          defaultValue: "Are you sure you want to delete this user?",
        })}
        onConfirm={() => handleDeleteUser(deleteModal.userId)}
        onClose={() => setDeleteModal({ show: false, userId: null })}
      />
    </UserPageShell>
  );
};

export default Users;
